import axios from 'axios'
import {useContext, useEffect, useState} from 'react'
import {obtenerVersionActual, PoblarCacheComun} from '../cache/servicios-cache'
import {ContextoAplicacion} from '../contexto'
import {
  getRestApi,
  idToken,
  refreshAccessToken,
} from '../microservicios/ApiAxios'
import {getMsalInstance, getLoginContext} from '../msalService'

const AxiosInterceptador = ({children}) => {
  const contextoAplicacion = useContext(ContextoAplicacion)

  const errorInterceptor = async (error) => {
    contextoAplicacion.setCargando(false)
    contextoAplicacion.setCountFetching((prv) => prv - 1)

    // 401: el backend rechazó el token. Forzamos un token nuevo y reintentamos
    // UNA sola vez. Si el refresh silencioso falla, mandamos a login interactivo.
    // La guarda _reintento401 evita bucles: un 401 persistente cae al modal normal.
    const solicitudOriginal = error.config
    if (
      error.response &&
      error.response.status === 401 &&
      solicitudOriginal &&
      !solicitudOriginal._reintento401
    ) {
      solicitudOriginal._reintento401 = true
      try {
        const cuenta = getMsalInstance().getAllAccounts()[0]
        const token = await getMsalInstance().acquireTokenSilent({
          account: cuenta,
          scopes: getLoginContext().scopes,
          forceRefresh: true,
        })
        solicitudOriginal.headers['Authorization'] = `Bearer ${token.accessToken}`
        solicitudOriginal.headers['IdToken'] = `${token.idToken}`
        return axios.request(solicitudOriginal)
      } catch (e) {
        return getMsalInstance().acquireTokenRedirect(getLoginContext())
      }
    }

    const respuesta = error.response.data
    contextoAplicacion.setModal({
      abierto: true,
      titulo:
        typeof respuesta === 'string'
          ? respuesta.trim().length > 0
            ? respuesta.trim()
            : 'Ocurrió un error, inténtelo más tarde'
          : typeof respuesta === 'object'
          ? respuesta.message
          : 'Ocurrió un error en el servicio, inténtelo más tarde',
      botones: [
        {
          nombre: 'Cerrar',
          click: () => {
            contextoAplicacion.setModal({abierto: false, botones: []})
          },
        },
      ],
    })

    return Promise.reject(error)
  }

  const completadoInterceptor = async (respuesta) => {
    contextoAplicacion.setCargando(false)
    contextoAplicacion.setCountFetching((prv) => prv - 1)
    if (respuesta.status === 201) {
      await PoblarCacheComun(true)
      respuesta.config.headers['VersionCache'] = await obtenerVersionActual()
      let nuevaRespuesta = await axios.request(respuesta.config)
      return nuevaRespuesta
    } else {
      return respuesta
    }
  }

  const requestInterceptor = async (config) => {
    contextoAplicacion.setCargando(true)
    contextoAplicacion.setCountFetching((prv) => prv + 1)
    const sessionData = await refreshAccessToken()
    if (sessionData) {
      config.headers['Authorization'] = `Bearer ${sessionData}`
      config.headers['IdToken'] = `${idToken}`
    }
    // axios 1.x: si el body es FormData no se fija Content-Type, el navegador
    // pone el boundary multipart (con application/json axios lo serializaria a JSON)
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json'
      config.headers['Accept'] = 'application/json'
    }
    config.headers['VersionCache'] = await obtenerVersionActual()
    return config
  }

  useEffect(() => {
    ;(async () => {
      const restApi = await getRestApi()
      const request = restApi.interceptors.request.use(requestInterceptor)
      const interceptor = restApi.interceptors.response.use(
        completadoInterceptor,
        errorInterceptor
      )
      contextoAplicacion.setConfigurado(true)

      return () => {
        restApi.interceptors.response.eject(interceptor)
        restApi.interceptors.request.eject(request)
      }
    })()
  }, [])

  return contextoAplicacion.configurado ? children : null
}

export default AxiosInterceptador
